import type { ComponentType } from "react";

import { QuestionnaireType } from "@/encounters/components/EncounterLabor/constants/labor-questionnaires";

import { BirthingFlowsheetDrawer } from "./birthing";
import { CervicalExamFlowsheetDrawer } from "./cervical-exam";
import { ContractionsFlowsheetDrawer } from "./contractions";
import { FetalHeartRateFlowsheetDrawer } from "./fetal-heart-rate";
import { InputOutputFlowsheetDrawer } from "./input-output";
import type { NativeLaborFlowsheetQuickAddDrawerProps } from "./interfaces";
import { LaborVitalsFlowsheetDrawer } from "./labor-vitals";
import { MembranesFlowsheetDrawer } from "./membranes";
import { PositionFlowsheetDrawer } from "./position";
import { RomFlowsheetDrawer } from "./rom";

const NATIVE_LABOR_FLOWSHEET_QUICK_ADD_DRAWERS: Partial<
  Record<QuestionnaireType, ComponentType<NativeLaborFlowsheetQuickAddDrawerProps>>
> = {
  [QuestionnaireType.Birthing]: BirthingFlowsheetDrawer,
  [QuestionnaireType.FetalHeartRate]: FetalHeartRateFlowsheetDrawer,
  [QuestionnaireType.LaborVitals]: LaborVitalsFlowsheetDrawer,
  [QuestionnaireType.Contractions]: ContractionsFlowsheetDrawer,
  [QuestionnaireType.CervicalExam]: CervicalExamFlowsheetDrawer,
  [QuestionnaireType.Membranes]: MembranesFlowsheetDrawer,
  [QuestionnaireType.RuptureOfMembranes]: RomFlowsheetDrawer,
  [QuestionnaireType.Position]: PositionFlowsheetDrawer,
  [QuestionnaireType.InputOutput]: InputOutputFlowsheetDrawer,
};

/**
 * Registers a native labor flowsheet quick-add drawer for a questionnaire type.
 * Returns an uninstall callback that restores the previous registration.
 */
export function registerNativeLaborFlowsheetQuickAddDrawer(
  questionnaireType: QuestionnaireType,
  drawer: ComponentType<NativeLaborFlowsheetQuickAddDrawerProps>,
): () => void {
  const previousDrawer = NATIVE_LABOR_FLOWSHEET_QUICK_ADD_DRAWERS[questionnaireType];
  NATIVE_LABOR_FLOWSHEET_QUICK_ADD_DRAWERS[questionnaireType] = drawer;

  return () => {
    if (previousDrawer === undefined) {
      delete NATIVE_LABOR_FLOWSHEET_QUICK_ADD_DRAWERS[questionnaireType];
      return;
    }

    NATIVE_LABOR_FLOWSHEET_QUICK_ADD_DRAWERS[questionnaireType] = previousDrawer;
  };
}

export function isNativeLaborFlowsheetDrawerQuestionnaire(
  questionnaireName: string,
): questionnaireName is QuestionnaireType {
  return questionnaireName in NATIVE_LABOR_FLOWSHEET_QUICK_ADD_DRAWERS;
}

export function getNativeLaborFlowsheetQuickAddDrawer(
  questionnaireName: QuestionnaireType,
): ComponentType<NativeLaborFlowsheetQuickAddDrawerProps> | undefined {
  return NATIVE_LABOR_FLOWSHEET_QUICK_ADD_DRAWERS[questionnaireName];
}
